import React, { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import P from '../../../../../../elements/Typo/P'
import Button from '../../../../../../elements/button'
import Icon from '../../../../../../components/icons'
import Text from '../../../../../../elements/Typo/Text'

import { useExperimentApi } from '../../../../../../store/api/hooks/experimentApiHook'
import { useNavigate, useParams } from 'react-router-dom'
import { URLS } from '../../../../../../_config'
import DataSelection from '../../../../../CreateExperiment/setScope/_components/uploadData/dataSelection'
import toast from 'react-hot-toast'
import Loader from '../../../../../../components/loader'
import Container from '../../../../../../elements/Container'
import DataFileUploader from '../../../../../../components/DataFileUploader'
import { SelectedDataTableType, UploadStatusType } from '../../../../../../_config/types'

interface Props {
    next: () => void
    popup: boolean
}

const MultiKpiUploadData: React.FC<Props> = ({ next, popup }) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const params = useParams()
    const experimentApi = useExperimentApi()
    const [working, setWorking] = useState<boolean>(false)
    const [selectedKPIMeasurementTable, setSelectedKPIMeasurementTable] = useState<SelectedDataTableType | null>(null)
    const [selectedRecommendationMeasurementTable, setSelectedRecommendationMeasurementTable] = useState<SelectedDataTableType | null>(null)
    const [kpiFileUploader, setKpiFileUploader] = useState<UploadStatusType>('ready-for-upload')
    const [recommendationFileUploader, setRecommendationFileUploader] = useState<UploadStatusType>('ready-for-upload')

    const updateWithMeasurementTableData = () => {
        // delta_measurement_table_name: selectedKPIMeasurementTable.name,
        const payload: any = {
            literal: params.experiment,
        }
        if (selectedKPIMeasurementTable) {
            payload.delta_measurement_table_name = selectedKPIMeasurementTable?.table.name
        }
        if (selectedRecommendationMeasurementTable) {
            payload.delta_recommendation_table_name = selectedRecommendationMeasurementTable?.table.name
        }
        setWorking(true)
        experimentApi.updateExperiment(
            payload,
            (data: any) => {
                setWorking(false)
                next()
            },
            (error: any) => {
                setWorking(false)
                toast.error(error?.message)
            },
        )
    }

    const isDataReday = () => {
        return (selectedKPIMeasurementTable || kpiFileUploader === 'data-ready') && (selectedRecommendationMeasurementTable || recommendationFileUploader === 'data-ready') && !working
    }

    return (
        <Container size="md">
            <p className="text-[1.125rem] font-bold mt-2">{t('Measurement Data Selection / Upload')}</p>

            <div className="grid grid-cols-2 gap-4 mt-3">
                <div>
                    <p className="text-[1rem] font-bold">{t('KPI Data:')}</p>
                    {!popup && (
                        <>
                            <Text size="sm" className="!text-[#676767] !text-[0.79rem]">
                                {t('Ensure your document includes these columns: Granularity, Date, KPI for seamless data processing.')}
                            </Text>
                            <P className="text-[#414040] font-semibold !mb-2 text-[0.677rem] mt-2">{t('Upload your own data')}</P>
                            <DataFileUploader data-testid="uploader-measurement" uploadType="measurement" literal={params.experiment as string} uploadingStatus={setKpiFileUploader} />

                            <div className="flex items-center justify-center my-4">
                                <div className="flex items-center justify-center leading-none text-[0.667rem] font-semibold w-[1.833rem] h-[1.833rem] bg-[#F4F4F4] border border-dashed border-[#BEBEBE] rounded-full">{t('OR')}</div>
                            </div>
                        </>
                    )}

                    <div className="flex-shrink-0 my-4">
                        <DataSelection data-testid="dataselection-measurement" _for="measurement" selectedDataTable={setSelectedKPIMeasurementTable} />
                    </div>
                </div>

                <div>
                    <p className="text-[1rem] font-bold">{t('Recommendation Data:')}</p>
                    {!popup && (
                        <>
                            <Text size="sm" className="!text-[#676767] !text-[0.79rem]">
                                {t('Ensure your document includes Granularity, Blocking Factors, TEST_GROUP, CONVERSION for seamless data processing.')}
                            </Text>
                            <P className="text-[#414040] font-semibold !mb-2 text-[0.677rem] mt-2">{t('Upload your own data')}</P>

                            <DataFileUploader data-testid="uploader-recommendation" uploadType="recommendation" literal={params.experiment as string} uploadingStatus={setRecommendationFileUploader} />

                            <div className="flex items-center justify-center my-4">
                                <div className="flex items-center justify-center leading-none text-[0.667rem] font-semibold w-[1.833rem] h-[1.833rem] bg-[#F4F4F4] border border-dashed border-[#BEBEBE] rounded-full">{t('OR')}</div>
                            </div>
                        </>
                    )}

                    <div className="flex-shrink-0 my-4">
                        <DataSelection data-testid="dataselection-recommendation" _for="recommendation" selectedDataTable={setSelectedRecommendationMeasurementTable} />
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-between my-4 ">
                <div className="flex items-center gap-3">
                    <Button
                        theme="secondary"
                        size="xs"
                        className="text-white"
                        onClick={() => {
                            void navigate(URLS.MY_EXPERIMENTS)
                        }}
                    >
                        {t('Previous Step')}
                    </Button>
                </div>
                <div className="flex items-center gap-3">
                    <Button
                        theme="secondary"
                        size="xs"
                        className="text-white"
                        disabled={!isDataReday()}
                        onClick={() => {
                            updateWithMeasurementTableData()
                        }}
                    >
                        {working ? <Loader /> : t('NEXT STEP')}
                    </Button>
                </div>
            </div>

            <div className="flex gap-2 items-center">
                <Icon name="envelop" security="0.7rem" />
                <Text size="xs">
                    <Trans i18nKey="Having trouble submitting your information? If your <bold>data</bold> is <bold>too large</bold>, please reach out to us directly via <bold>email</bold>" components={{ bold: <b /> }} />
                </Text>
            </div>
        </Container>
    )
}

export default MultiKpiUploadData
